import openai from './openaiClient';
import { 
  APIConnectionError,
  AuthenticationError,
  PermissionDeniedError,
  RateLimitError,
  InternalServerError
} from 'openai';

/**
 * Maps OpenAI API error types to user-friendly error messages.
 * @param {Error} error - The error object from OpenAI API.
 * @returns {Object} Error information with isInternal flag and message.
 */
function getErrorMessage(error) {
  if (error instanceof AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits or do not have access to this resource.' };
  } else if (error instanceof RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. You are sending requests too quickly. Please wait a moment and try again.' };
  } else if (error instanceof InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

/**
 * Generates AI-powered trip plan with hotel and restaurant recommendations.
 * @param {Object} tripDetails - Trip planning parameters.
 * @returns {Promise<Object>} Structured trip plan with hotels and restaurants.
 */
export async function generateTripPlan(tripDetails) {
  const { location, days, budget } = tripDetails;

  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5',
      messages: [
        { 
          role: 'system', 
          content: 'You are an expert travel planner specializing in Philippine destinations. Provide detailed, realistic recommendations with accurate pricing in Philippine Pesos (PHP). Include multiple options for hotels and restaurants at different price points.' 
        },
        { 
          role: 'user', 
          content: `Create a ${days}-day trip plan for ${location}, Philippines with a total budget of PHP ${budget}. Include hotel recommendations and restaurant suggestions for each day with realistic pricing.` 
        },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'trip_plan_response',
          schema: {
            type: 'object',
            properties: {
              destination: { type: 'string' },
              totalDays: { type: 'number' },
              estimatedBudget: { type: 'number' },
              hotels: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    id: { type: 'string' },
                    name: { type: 'string' },
                    category: { type: 'string' },
                    pricePerNight: { type: 'number' },
                    totalCost: { type: 'number' },
                    rating: { type: 'number' },
                    amenities: { type: 'array', items: { type: 'string' } },
                    location: { type: 'string' },
                    description: { type: 'string' },
                    isSelected: { type: 'boolean' }
                  },
                  required: ['id', 'name', 'category', 'pricePerNight', 'totalCost', 'rating', 'amenities', 'location', 'description', 'isSelected']
                }
              },
              restaurants: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    id: { type: 'string' },
                    name: { type: 'string' },
                    cuisine: { type: 'string' },
                    pricePerMeal: { type: 'number' },
                    totalCost: { type: 'number' },
                    rating: { type: 'number' },
                    specialties: { type: 'array', items: { type: 'string' } },
                    location: { type: 'string' },
                    description: { type: 'string' },
                    mealType: { type: 'string' },
                    day: { type: 'number' },
                    isSelected: { type: 'boolean' }
                  },
                  required: ['id', 'name', 'cuisine', 'pricePerMeal', 'totalCost', 'rating', 'specialties', 'location', 'description', 'mealType', 'day', 'isSelected']
                }
              },
              alternatives: {
                type: 'object',
                properties: {
                  hotels: {
                    type: 'array',
                    items: {
                      type: 'object',
                      properties: {
                        id: { type: 'string' },
                        name: { type: 'string' },
                        category: { type: 'string' },
                        pricePerNight: { type: 'number' },
                        totalCost: { type: 'number' },
                        rating: { type: 'number' },
                        amenities: { type: 'array', items: { type: 'string' } },
                        location: { type: 'string' },
                        description: { type: 'string' },
                        isSelected: { type: 'boolean' }
                      },
                      required: ['id', 'name', 'category', 'pricePerNight', 'totalCost', 'rating', 'amenities', 'location', 'description', 'isSelected']
                    }
                  },
                  restaurants: {
                    type: 'array',
                    items: {
                      type: 'object',
                      properties: {
                        id: { type: 'string' },
                        name: { type: 'string' },
                        cuisine: { type: 'string' },
                        pricePerMeal: { type: 'number' },
                        totalCost: { type: 'number' },
                        rating: { type: 'number' },
                        specialties: { type: 'array', items: { type: 'string' } },
                        location: { type: 'string' },
                        description: { type: 'string' },
                        mealType: { type: 'string' },
                        day: { type: 'number' },
                        isSelected: { type: 'boolean' }
                      },
                      required: ['id', 'name', 'cuisine', 'pricePerMeal', 'totalCost', 'rating', 'specialties', 'location', 'description', 'mealType', 'day', 'isSelected']
                    }
                  }
                },
                required: ['hotels', 'restaurants']
              },
              summary: { type: 'string' }
            },
            required: ['destination', 'totalDays', 'estimatedBudget', 'hotels', 'restaurants', 'alternatives', 'summary'],
            additionalProperties: false,
          },
        },
      },
      reasoning_effort: 'medium',
      verbosity: 'medium',
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error generating trip plan:', error);
    }
    throw new Error(errorInfo.message);
  }
}